import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import type { IRequestCriteriaData } from './RequestCriteria';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    AnchorLink,
    ButtonContainer,
    Form,
    FormDivider,
    Icon,
    IconColor,
    InputRadioGroup,
    InputTextArea,
    ReadOnlyInputText,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { RequestCriteria } from './RequestCriteria';

export const gebruiktBetablokkerOptions = ['Ja', 'Nee'];

export interface ICustomControlData {
    gebruiktBetablokker: boolean | null;
    indicationOutcomeText?: string;
    lichamelijkOnderzoek: string | null;
    opmerkingen: string | null;
    requestCriteriaData: IRequestCriteriaData;
    voorgeschiedenis_klachten: string | null;
}

interface IInnerTiecgV1RequestAddEditState {
    buttonDisabled?: boolean;
    showCriteria: boolean;
}

export function TiecgV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <InnerTiecgV1RequestAddEdit
            {...props}
            {...episodeContext}
        />
    );
}

class InnerTiecgV1RequestAddEdit extends Component<
    ICustomControlRequestAddEditProps & IEpisodeContext,
    IInnerTiecgV1RequestAddEditState
> {
    constructor(props: ICustomControlRequestAddEditProps & IEpisodeContext) {
        super(props);

        this.state = {
            showCriteria: !this.props.request.id,
        };
    }

    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isExaminationRequest
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                <Form onSubmit={async () => null}>
                    {this.state.showCriteria ? <>{this.requestCriteriaForm()} </> : <> {this.addEditForm()} </>}
                </Form>
            </NewOrEditRequestProcessContainer>
        );
    }

    private requestCriteriaForm() {
        return (
            <RequestCriteria
                customControlData={this.props.request.customControlData}
                handleChange={async (values) => await this.handleChangeCustomControl(values)}
                hideCriteria={async (hide) => await this.setState({ showCriteria: !hide })}
                onCancel={this.props.onCancel}
                patientId={this.props.patient.id}
                service={this.props.service}
            />
        );
    }

    private addEditForm() {
        return (
            <>
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.request}
                />

                <ServiceOrganizationProvider
                    errors={this.props.errors}
                    initialOrganizationId={this.props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={this.props.request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    onChange={async (organizationId, practitionerId) => {
                        await this.props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await this.props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await this.props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={this.props.service.id}
                />

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.voorgeschiedenis_klachten')}>
                    <FormLabel isRequired>Voorgeschiedenis en klachten</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('customControlData.voorgeschiedenis_klachten')}
                            onChange={async (value) =>
                                await this.handleChangeCustomControl({ voorgeschiedenis_klachten: value })
                            }
                            onDoValidate={async () => await this.validateOneCustomControl('voorgeschiedenis_klachten')}
                            placeholder="Geef een korte omschrijving..."
                            value={this.props.request.customControlData.voorgeschiedenis_klachten}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup>
                    <FormLabel isRequired>Indicatie</FormLabel>
                    <InputContainer>
                        <ReadOnlyInputText value={this.props.request.customControlData.indicationOutcomeText || null} />
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup>
                    <FormLabel isRequired>Bloeddruk</FormLabel>
                    <InputContainer isFormControl>
                        <InputControl inline>
                            Systolisch {this.props.request.customControlData.requestCriteriaData.bloeddruk_systolisch}
                            mmHg
                        </InputControl>
                        <InputControl inline>
                            Diastolisch {this.props.request.customControlData.requestCriteriaData.bloeddruk_diastolisch}
                            mmHg
                        </InputControl>
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.lichamelijkOnderzoek')}>
                    <FormLabel isRequired>Lichamelijk onderzoek</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={this.props.errors.get('customControlData.lichamelijkOnderzoek')}
                            onChange={async (value) =>
                                await this.handleChangeCustomControl({ lichamelijkOnderzoek: value })
                            }
                            onDoValidate={async () => await this.validateOneCustomControl('lichamelijkOnderzoek')}
                            placeholder="Bevindingen hartauscultatie..."
                            value={this.props.request.customControlData.lichamelijkOnderzoek}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.gebruiktBetablokker')}>
                    <FormLabel isRequired>Gebruikt bètablokker</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            automation="gebruiktBetablokker"
                            error={this.props.errors.get('customControlData.gebruiktBetablokker')}
                            name="gebruiktBetablokker"
                            onChange={async (value) =>
                                await this.handleChangeCustomControl({
                                    gebruiktBetablokker: value === gebruiktBetablokkerOptions[0] ? true : false,
                                })
                            }
                            onDoValidate={async () => await this.validateOneCustomControl('gebruiktBetablokker')}
                            options={gebruiktBetablokkerOptions}
                            value={
                                this.props.request.customControlData.gebruiktBetablokker
                                    ? gebruiktBetablokkerOptions[0]
                                    : this.props.request.customControlData.gebruiktBetablokker !== null
                                      ? gebruiktBetablokkerOptions[1]
                                      : null
                            }
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.opmerkingen')}>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('customControlData.opmerkingen')}
                            onChange={async (value) => await this.handleChangeCustomControl({ opmerkingen: value })}
                            onDoValidate={async () => await this.validateOneCustomControl('opmerkingen')}
                            placeholder={translateKey('common.remarksOrQuestions')}
                            value={this.props.request.customControlData.opmerkingen}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                <FormGroup>
                    <AnchorLink
                        openInNewTab
                        to="/assets/documents/Inspannings_ECG_IECG_v1_112015.pdf"
                    >
                        <Icon
                            color={IconColor.secondary}
                            icon="external"
                        />
                        Patiëntinformatie over ECG onderzoek
                    </AnchorLink>
                </FormGroup>
                <FormDivider />

                <PatientContactAddEdit {...this.props} />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!this.props.request.id}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </>
        );
    }

    private async handleChangeCustomControl(value: Partial<ICustomControlData>) {
        await this.props.handleChangeCustomControl(value);
    }

    private async validateOneCustomControl(name: keyof ICustomControlData) {
        await this.props.validateOneCustomControl(name);
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.props.onSave(requestId);
    }
}

export function tiecgV1InitializeCustomControlData() {
    return {
        requestCriteriaData: {
            retrosternale_pijnklachten: false,
            provocatie_klachten: false,
            verdwijning_klachten: false,
            coronairSclerosePercentage: 0,
            contra_indicaties: null,
            bloeddruk_systolisch: null,
            bloeddruk_diastolisch: null,
        },
        indicationOutcomeText: null,
        voorgeschiedenis_klachten: null,
        lichamelijkOnderzoek: null,
        gebruiktBetablokker: null,
        opmerkingen: null,
        contactgegevens_patient: {
            emailadres: null,
            mobileNumber: null,
            homePhoneNumber: null,
        },
    };
}
